import { clsx } from 'clsx';
import {
  forwardRef,
  memo,
  useRef,
  type ComponentPropsWithoutRef,
  type MutableRefObject,
  type RefObject,
} from 'react';
import { useIntl } from 'react-intl';

import { ChevronSmallLeftDoubleIcon, XmarkIcon } from '@dynatrace/strato-icons';

import { SidebarHint } from './CollapsedSidebarRail.js';
import * as styles from './SidebarLayout.sty.js';
import {
  type ExtractedContentSlot,
  type ExtractedSidebarSlot,
} from './slots.js';
import { useButtonFocusHandover } from './useFocusHandover.js';
import { useLayoutState } from './useLayoutState.js';
import { Button } from '../../../buttons/button/Button.js';
import { useId } from '../../../core/hooks/useId.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { Drawer } from '../../../overlays/drawer/Drawer.js';
import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';
import { SplitLayout } from '../../split-layout/SplitLayout.js';
import { useTeleport } from '../shared/useTeleport.js';

type TeleportPair = ReturnType<typeof useTeleport>;

function SidebarLayoutInner({
  sidebar,
  content,
  containerRef,
  panelId,
  persistedPanelWidthRef,
  contentTeleport,
  panelTeleport,
}: {
  sidebar: ExtractedSidebarSlot;
  content: ExtractedContentSlot;
  containerRef: RefObject<HTMLDivElement>;
  panelId: string;
  persistedPanelWidthRef: MutableRefObject<number | undefined>;
  contentTeleport: TeleportPair;
  panelTeleport: TeleportPair;
}) {
  const layout = useLayoutState({ sidebar, containerRef });
  const intl = useIntl();
  const focusHandover = useButtonFocusHandover();

  const expandSidebarLabel = intl.formatMessage({
    defaultMessage: 'Expand sidebar',
    id: 'NF0tLfJTokD/xw5B',
    description: 'Label for the button that expands the sidebar.',
  });

  const collapseSidebarLabel = intl.formatMessage({
    defaultMessage: 'Collapse sidebar',
    id: 'LJ0fyD2++VZgZ5g0',
    description: 'Label for the button that collapses the sidebar.',
  });

  const sidebarLabel = intl.formatMessage({
    defaultMessage: 'Sidebar',
    id: 'BYUJpW8TBonAxnYd',
    description: 'Accessible label for the sidebar panel.',
  });

  const setCollapsedWithFocusHandover = (value: boolean) => {
    layout.setCollapsed(value);
    focusHandover.setTarget(value ? 'expand' : 'collapse');
  };

  switch (layout.mode) {
    case 'collapsed': {
      return (
        <div className={styles.collapsedSidebarWrapper}>
          <SidebarHint
            panelId={panelId}
            expandSidebarLabel={expandSidebarLabel}
            expandButtonRef={focusHandover.expandButtonRef}
            onCollapsedChange={setCollapsedWithFocusHandover}
          />
          <div
            ref={content.ref}
            {...content.domProps}
            className={clsx(styles.content, content.domProps.className)}
          >
            <contentTeleport.Target />
          </div>
        </div>
      );
    }
    case 'split': {
      const splitLayoutKey =
        persistedPanelWidthRef.current === undefined
          ? `split-${String(sidebar.config.defaultWidth)}`
          : 'split-resized';

      return (
        <SplitLayout key={splitLayoutKey}>
          <SplitLayout.Panel
            {...sidebar.domProps}
            ref={sidebar.ref}
            minWidth={sidebar.config.minWidth}
            maxWidth={sidebar.config.maxWidth}
            defaultWidth={
              persistedPanelWidthRef.current ?? sidebar.config.defaultWidth
            }
            resizable={sidebar.config.resizable}
            position="start"
            onResize={(value) => {
              persistedPanelWidthRef.current = value;
              sidebar.config.onResize?.(value);
            }}
            className={clsx(styles.panel, sidebar.domProps.className)}
            aria-label={sidebarLabel}
            id={panelId}
          >
            <panelTeleport.Target />
          </SplitLayout.Panel>
          <SplitLayout.ResizeHandle className={styles.resizeHandle}>
            <div className={styles.collapseButtonContainer}>
              <Tooltip text={collapseSidebarLabel}>
                <Button
                  className={styles.collapseButton}
                  aria-expanded="true"
                  ref={focusHandover.collapseButtonRef}
                  aria-controls={panelId}
                  aria-label={collapseSidebarLabel}
                  size="condensed"
                  data-dt-name="ExpandCollapse"
                  data-dt-properties="state:collapse"
                  onClick={() => {
                    setCollapsedWithFocusHandover(true);
                  }}
                >
                  <Button.Prefix>
                    <ChevronSmallLeftDoubleIcon />
                  </Button.Prefix>
                </Button>
              </Tooltip>
            </div>
          </SplitLayout.ResizeHandle>
          <SplitLayout.Content
            {...content.domProps}
            ref={content.ref}
            minWidth={content.config.minWidth}
            className={clsx(styles.content, content.domProps.className)}
          >
            <contentTeleport.Target />
          </SplitLayout.Content>
        </SplitLayout>
      );
    }
    case 'drawer': {
      const drawerContentArea = (
        <div
          ref={content.ref}
          {...content.domProps}
          className={clsx(styles.content, content.domProps.className)}
        >
          <contentTeleport.Target />
        </div>
      );

      return (
        <>
          <div className={styles.collapsedSidebarWrapper}>
            <SidebarHint
              panelId={panelId}
              expandSidebarLabel={expandSidebarLabel}
              expandButtonRef={focusHandover.expandButtonRef}
              isCollapsed={layout.collapsed}
              onCollapsedChange={setCollapsedWithFocusHandover}
            />
            {drawerContentArea}
          </div>
          <Drawer
            {...sidebar.domProps}
            ref={sidebar.ref}
            className={clsx(styles.drawer, sidebar.domProps.className)}
            aria-label={sidebarLabel}
            isDismissed={layout.collapsed}
            onDismiss={(state, _reason) => {
              setCollapsedWithFocusHandover(state);
            }}
            placement="left"
            width="wide"
          >
            <div className={styles.drawerContentContainer}>
              <div className={styles.drawerHeader}>
                <Button
                  style={{ marginLeft: 'auto' }}
                  ref={focusHandover.collapseButtonRef}
                  aria-label={collapseSidebarLabel}
                  data-dt-name="CloseX"
                  onClick={() => {
                    setCollapsedWithFocusHandover(true);
                  }}
                >
                  <Button.Prefix>
                    <XmarkIcon />
                  </Button.Prefix>
                </Button>
              </div>
              <div className={styles.drawerContent}>
                <panelTeleport.Target />
              </div>
            </div>
          </Drawer>
        </>
      );
    }
  }
}

/** @internal */
export type LayoutRootProps = {
  sidebar: ExtractedSidebarSlot | null;
  content: ExtractedContentSlot;
} & Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'content'>;

/** @internal */
export const SidebarLayout = memo(
  forwardRef<HTMLDivElement, LayoutRootProps>((props, ref) => {
    const { className: rootClassName, sidebar, content, ...rootProps } = props;

    const panelId = useId();
    const containerRef = useRef<HTMLDivElement>(null);
    const rootRef = useMergeRefs([ref, containerRef]);
    const persistedPanelWidth = useRef<number>();

    const contentTeleport = useTeleport();
    const panelTeleport = useTeleport();

    return (
      <>
        <contentTeleport.Source>{content.children}</contentTeleport.Source>
        {sidebar && (
          <panelTeleport.Source>{sidebar.children}</panelTeleport.Source>
        )}
        <div
          {...rootProps}
          ref={rootRef}
          className={clsx(styles.layoutRoot, rootClassName)}
        >
          {sidebar ? (
            <SidebarLayoutInner
              sidebar={sidebar}
              content={content}
              containerRef={containerRef}
              panelId={panelId}
              persistedPanelWidthRef={persistedPanelWidth}
              contentTeleport={contentTeleport}
              panelTeleport={panelTeleport}
            />
          ) : (
            <div
              ref={content.ref}
              {...content.domProps}
              className={clsx(styles.content, content.domProps.className)}
            >
              <contentTeleport.Target />
            </div>
          )}
        </div>
      </>
    );
  }),
);
SidebarLayout.displayName = 'SidebarLayout (forwardRef)';
